import { useState, useCallback, useEffect, useMemo } from 'react';
import type { DataSourceType, ColumnType, FilterType, SortConfigType, DataRowType, FilterOperatorType } from '@/types/prompt';
import { ANALYSIS_PROMPT_TEMPLATE } from '@/lib/prompts';
import { generateTextGeminiClientSide } from '@/services/client-ai-api';
import { Model } from '@/types/ai';
import { MODELS } from '@/config/ai-models';

// Refined Predefined Questions (more direct)
export const PREDEFINED_QUESTIONS = [
    "Identify the top 3-5 performers based on the primary sort metric. Include key supporting metrics.",
    "Identify the worst 3-5 performers based on the primary sort metric. Include key supporting metrics.",
    "Analyze the trend of the primary sort metric over the available period (if applicable). Are there significant changes?",
    "Provide 2-3 specific, actionable recommendations to improve performance based *only* on the data provided. Explain the reasoning clearly.",
    "Are there any potential correlations between different metrics in this data (e.g., does higher CTR correlate with lower CPA)?",
    "Summarize the overall performance shown in this data slice and state the single most important takeaway."
];
export const PREVIEW_ROW_OPTIONS = [5, 10, 50, 100, 500];

// Select the desired model ID from the central config
// Ensure the ID matches exactly what's in ai-models.ts
const PROMPT_BUILDER_GEMINI_MODEL_ID = MODELS.gemini.find(m => m.id === 'gemini-2.0-flash')?.id || MODELS.gemini[0].id; // Fallback to first gemini model ID

// Helper function - keep it co-located or move to utils
const getFilterOperatorsForType = (type: 'metric' | 'dimension' | 'date'): { label: string; value: FilterOperatorType }[] => {
    const commonString: { label: string; value: FilterOperatorType }[] = [
        { label: 'Contains', value: 'contains' }, { label: 'Does Not Contain', value: 'does_not_contain' },
        { label: 'Equals', value: 'equals' }, { label: 'Not Equals', value: 'not_equals' },
    ];
    const numeric: { label: string; value: FilterOperatorType }[] = [
        { label: 'Greater Than or Equals', value: 'greater_than_equals' }, { label: 'Greater Than', value: 'greater_than' },
        { label: 'Less Than or Equals', value: 'less_than_equals' }, { label: 'Less Than', value: 'less_than' },
        { label: 'Equals', value: 'equals_number' }, { label: 'Not Equals', value: 'not_equals' },
    ];
    const date: { label: string; value: FilterOperatorType }[] = [
        { label: 'On or After', value: 'greater_than_equals' }, { label: 'After', value: 'greater_than' },
        { label: 'On or Before', value: 'less_than_equals' }, { label: 'Before', value: 'less_than' },
        { label: 'Equals', value: 'equals' }, { label: 'Not Equals', value: 'not_equals' },
    ];
    switch (type) {
        case 'metric': return numeric;
        case 'date': return date;
        case 'dimension': default: return commonString;
    }
};

// --- The Custom Hook ---
export function usePromptBuilderState() {
    // --- State Declarations --- 
    const [selectedDataSource, setSelectedDataSource] = useState<DataSourceType | null>(null);
    const [columns, setColumns] = useState<ColumnType[]>([]);
    const [filters, setFilters] = useState<FilterType[]>([]);
    const [editingFilterId, setEditingFilterId] = useState<number | null>(null);
    const [sortConfig, setSortConfig] = useState<SortConfigType>({ key: '', direction: 'desc' });
    const [limitRows, setLimitRows] = useState<boolean>(true);
    const [selectedPredefinedQuestions, setSelectedPredefinedQuestions] = useState<string[]>(PREDEFINED_QUESTIONS);
    const [customQuestions, setCustomQuestions] = useState<string[]>(['']);
    const [generatedPromptText, setGeneratedPromptText] = useState<string>('');
    const [filteredData, setFilteredData] = useState<DataRowType[]>([]);
    const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isGeneratingPrompt, setIsGeneratingPrompt] = useState<boolean>(false);
    const [isSubmittingAnalysis, setIsSubmittingAnalysis] = useState<boolean>(false);
    const [analysisResult, setAnalysisResult] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [previewRowsPerPage, setPreviewRowsPerPage] = useState<number>(PREVIEW_ROW_OPTIONS[0]);
    const [previewCurrentPage, setPreviewCurrentPage] = useState<number>(1);
    const [showResultCard, setShowResultCard] = useState<boolean>(false);

    // --- Callbacks --- 
    const addFilter = useCallback(() => {
        const newId = Date.now();
        let defaultOperator: FilterOperatorType = 'contains';
        if (columns.length > 0 && columns[0].type === 'metric') {
            defaultOperator = 'greater_than_equals';
        }
        const newFilter: FilterType = { id: newId, field: '', operator: defaultOperator, value: '' };
        setFilters(prev => [...prev, newFilter]);
        setEditingFilterId(newId);
    }, [columns]);

    const updateFilter = useCallback((id: number, key: keyof FilterType, value: any) => {
        setFilters(prev => prev.map(f => {
            if (f.id === id) {
                const updatedFilter = { ...f, [key]: value };
                if (key === 'field') {
                    const newColumn = columns.find(c => c.field === value);
                    const newType = newColumn?.type || 'dimension';
                    const newOperators = getFilterOperatorsForType(newType);
                    updatedFilter.operator = newOperators.length > 0 ? newOperators[0].value : 'contains';
                    updatedFilter.value = '';
                }
                return updatedFilter;
            }
            return f;
        }));
    }, [columns]);

    const removeFilter = useCallback((id: number) => {
        setFilters(prev => prev.filter(f => f.id !== id));
        if (editingFilterId === id) {
            setEditingFilterId(null);
        }
    }, [editingFilterId]);

    const handleSort = useCallback((key: string) => {
        setPreviewCurrentPage(1);
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    }, []);

    const handlePredefinedQuestionChange = useCallback((question: string, checked: boolean) => {
        setSelectedPredefinedQuestions(prev =>
            checked ? [...prev, question] : prev.filter(q => q !== question)
        );
    }, []);

    const handleCustomQuestionChange = useCallback((index: number, value: string) => {
        setCustomQuestions(prev => prev.map((q, i) => (i === index ? value : q)));
    }, []);

    const addCustomQuestion = useCallback(() => {
        if (customQuestions.length < 3) {
            setCustomQuestions(prev => [...prev, '']);
        }
    }, [customQuestions.length]);

    const removeCustomQuestion = useCallback((index: number) => {
        if (customQuestions.length > 1) {
            setCustomQuestions(prev => prev.filter((_, i) => i !== index));
        } else if (customQuestions.length === 1 && index === 0) {
            setCustomQuestions(['']);
        }
    }, [customQuestions.length]);

    const generatePrompt = useCallback(() => {
        if (!selectedDataSource) {
            setError('Cannot generate prompt: Please select a data source.');
            setGeneratedPromptText('');
            return;
        }
        setIsGeneratingPrompt(true);
        setError(null);
        setGeneratedPromptText('');
        setAnalysisResult(null);
        setShowResultCard(false);

        const sortCol = columns.find(c => c.field === sortConfig.key);
        const sortColumnName = sortCol?.name || sortConfig.key || 'Default Order';
        const sortOrder = sortConfig.key ? (sortConfig.direction === 'desc' ? 'High to Low' : 'Low to High') : '';
        const sortCriteria = sortConfig.key ? `${sortColumnName} (${sortOrder})` : 'Default order';

        const filtersApplied = filters.filter(f => f.field && f.operator).length > 0
            ? `Filters applied (${filters.filter(f => f.field && f.operator).length})`
            : 'No filters applied';

        const rowsToAnalyze = limitRows ? Math.min(filteredRowCount, 500) : filteredRowCount;
        const rowInfo = `${rowsToAnalyze} rows to be analyzed` + (limitRows && filteredRowCount > 500 ? ` (limited from ${filteredRowCount})` : '');

        const allQuestions = [
            ...selectedPredefinedQuestions,
            ...customQuestions.filter(q => q.trim() !== '')
        ];
        const userQuerySection = allQuestions.length > 0
            ? allQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n')
            : 'Provide a general analysis based on the configuration.';

        let prompt = ANALYSIS_PROMPT_TEMPLATE
            .replace('{{DATA_SOURCE}}', selectedDataSource.name)
            .replace('{{FILTER_CRITERIA}}', filtersApplied)
            .replace('{{SORT_CRITERIA}}', sortCriteria)
            .replace('{{TIME_FRAME}}', 'N/A - Time frame selection removed')
            .replace('{{USER_QUERY}}', userQuerySection);

        prompt += `\n\nAnalysis based on: ${rowInfo}.`;

        setGeneratedPromptText(prompt);
        setIsGeneratingPrompt(false);
    }, [selectedDataSource, filters, sortConfig, columns, filteredRowCount, limitRows, selectedPredefinedQuestions, customQuestions]);

    const handleRunAnalysis = useCallback(async () => {
        if (!generatedPromptText) {
            setError('Please generate a prompt first.');
            return;
        }
        if (filteredData.length === 0) {
            setError("Cannot run analysis with zero data rows.");
            return;
        }

        setIsSubmittingAnalysis(true);
        setShowResultCard(true);
        setError(null);
        setAnalysisResult(null);

        const dataToSend = limitRows ? filteredData.slice(0, 500) : filteredData;
        const cleanedData = dataToSend.map(row =>
            Object.fromEntries(Object.entries(row).filter(([_, v]) => v !== null && v !== undefined))
        );

        let formattedData = '[]';
        try {
            formattedData = JSON.stringify(cleanedData, null, 2);
        } catch (stringifyError) {
            console.error("Error stringifying data for prompt:", stringifyError);
            setError("Failed to prepare data for analysis.");
            setIsSubmittingAnalysis(false);
            return;
        }

        const finalPrompt = generatedPromptText + `\n\n<data>\n${formattedData}\n</data>`;

        console.log("Submitting to Client-Side AI using model:", PROMPT_BUILDER_GEMINI_MODEL_ID);

        try {
            const response = await generateTextGeminiClientSide(finalPrompt, PROMPT_BUILDER_GEMINI_MODEL_ID);

            if ('text' in response) {
                console.log("Client-Side AI Success (Prompt Page).");
                setAnalysisResult(response.text);
                setError(null);
            } else {
                console.error("Client-Side AI Error (Prompt Page):", response.error);
                setError(response.error || 'Client-side AI call failed with unspecified error.');
                setAnalysisResult(null);
            }
        } catch (err: any) {
            console.error("Error during client-side AI call execution (Prompt Page):", err);
            setError(err.message || 'An unexpected error occurred while calling the AI service.');
            setAnalysisResult(null);
        } finally {
            setIsSubmittingAnalysis(false);
        }
    }, [generatedPromptText, filteredData, limitRows]);

    useEffect(() => {
        let rawData: DataRowType[] = [];
        let processedData = [...rawData];

        if (filters.length > 0 && columns.length > 0) {
            processedData = processedData.filter(row => {
                return filters.every(filter => {
                    if (filter.field && filter.operator) {
                        const value = row[filter.field as keyof DataRowType];
                        switch (filter.operator) {
                            case 'contains':
                                return value && value.toString().includes(filter.value);
                            case 'does_not_contain':
                                return !value || !value.toString().includes(filter.value);
                            case 'equals':
                                return value === filter.value;
                            case 'not_equals':
                                return value !== filter.value;
                            case 'greater_than_equals':
                                return value >= filter.value;
                            case 'greater_than':
                                return value > filter.value;
                            case 'less_than_equals':
                                return value <= filter.value;
                            case 'less_than':
                                return value < filter.value;
                            case 'equals_number':
                                return value === filter.value;
                            default:
                                return true;
                        }
                    }
                    return true;
                });
            });
        }

        if (sortConfig.key && columns.find(c => c.field === sortConfig.key)) {
            processedData.sort((a, b) => {
                const aValue = a[sortConfig.key as keyof DataRowType];
                const bValue = b[sortConfig.key as keyof DataRowType];
                if (aValue === bValue) return 0;
                if (aValue > bValue) return sortConfig.direction === 'desc' ? -1 : 1;
                return sortConfig.direction === 'desc' ? 1 : -1;
            });
        }

        setFilteredData(processedData);
        setFilteredRowCount(processedData.length);
    }, [filters, sortConfig, columns]);

    return {
        selectedDataSource,
        columns,
        filters,
        editingFilterId,
        sortConfig,
        limitRows,
        selectedPredefinedQuestions,
        customQuestions,
        generatedPromptText,
        filteredData,
        filteredRowCount,
        isLoading: isGeneratingPrompt || isSubmittingAnalysis,
        isGeneratingPrompt,
        isSubmittingAnalysis,
        analysisResult,
        error,
        previewRowsPerPage,
        previewCurrentPage,
        showResultCard,
        setSelectedDataSource,
        setColumns,
        setFilters,
        setEditingFilterId,
        setSortConfig,
        setLimitRows,
        setSelectedPredefinedQuestions,
        setCustomQuestions,
        setGeneratedPromptText,
        setAnalysisResult,
        setError,
        setPreviewRowsPerPage,
        setPreviewCurrentPage,
        setFilteredData,
        setFilteredRowCount,
        setShowResultCard,
        addFilter,
        updateFilter,
        removeFilter,
        handleSort,
        handlePredefinedQuestionChange,
        handleCustomQuestionChange,
        addCustomQuestion,
        removeCustomQuestion,
        getFilterOperatorsForType,
        generatePrompt,
        handleRunAnalysis,
    };
} 